import { createSlice } from '@reduxjs/toolkit';
import {
  types as rs,
  FoodSet,
  priceMenuSet,
  getAvailableMenuDef,
} from 'earn-ts';
import {
  getMenuThunk,
  postOrderThunk,
  postItemsToOrderThunk,
  getCurrentOrderThunk,
} from './orderThunk';
import _ from 'lodash';
import { gentoken, updateSetPrices } from 'src/utils';

// candidate to store in localStorage
export interface ISessionState {
  kind: 'Table' | 'Takeout' | null;
  code: string | null;
  childId: string;
}

export interface IGroupOptionsState {
  sectionId: string;
  foodId: string;
}

// done is indicated by fullOrder actually being filled
export type TLoadOrderStatus = null | 'pending' | 'error';

export interface IOrderState {
  shopId: string;
  // todo would be nice to move menu def fields into this container object
  menuDef: rs.MenuDef | null;
  categories: rs.MenuCat[] | null;
  menus: { [key: string]: rs.MenuSet };
  lists: { [key: string]: rs.MenuList };
  foods: { [key: string]: rs.FoodItem };
  optionGroup: { [key: string]: rs.FoodOptionGroup };

  idCategory: string;
  listMenuSets: Record<string, FoodSet[]>;
  draftSet?: rs.OrderMenuSet;
  /// this is definition
  selectedSet: rs.MenuSet;
  /// current one, for use in currently open set editor
  foodSet: FoodSet | null;
  /// the sets that were not sent to backend yet. NOTE their ids will be replaced by backend
  /// so we clear this array when submitting because merging is also done by id.
  dataSelectedMenus: Array<rs.OrderMenuSet>;

  costDetail: {
    totalCost: number;
    basePrice: number;
  };
  priceMenus: Record<string, number>;
  isEditMenu: boolean;

  optionsPage?: IGroupOptionsState;

  session: ISessionState;
  /// contains the sets that were already sent to backend
  fullOrder: rs.OrderContainer | null;
  loadOrderStatus: TLoadOrderStatus;
  /// for adding items to order
  submittingStatus: null | 'pending' | 'error'; // error is after multiple retries
  titleHeader: string;
  tables: rs.TableGroupDef[];
}

const initialState: IOrderState = {
  menuDef: null,
  shopId: '',
  foods: {},
  categories: null,
  menus: {},
  lists: {},
  optionGroup: {},
  idCategory: '',
  listMenuSets: {},
  selectedSet: {} as rs.MenuSet,
  dataSelectedMenus: [],
  priceMenus: {},
  isEditMenu: false,
  foodSet: null,
  session: {
    kind: null,
    code: null,
    childId: '',
  },
  fullOrder: null,
  submittingStatus: null,
  loadOrderStatus: null,
  draftSet: undefined,
  costDetail: {
    totalCost: 0,
    basePrice: 0,
  },
  titleHeader: '',
  tables: [],
};

const storeMenuDef = (state: IOrderState, menuDef: rs.MenuDef) => {
  const availableMenuDef = getAvailableMenuDef({
    ...menuDef,
  });

  state.menuDef = availableMenuDef as rs.MenuDef;
  state.foods = availableMenuDef.foods;
  state.categories = availableMenuDef.categories;
  state.menus = availableMenuDef.menus;
  state.lists = availableMenuDef.lists;
  state.optionGroup = availableMenuDef.option_groups;
};

export const canModifySelectedMenus = (state: IOrderState) => {
  // not if items are currently being submitted
  return state.submittingStatus == null;
};

export const hasOptions = (
  menudef: rs.MenuDef,
  foodId: string,
  onlyRequired: boolean,
) => {
  let option_groups = Object.fromEntries(
    menudef.foods[foodId].option_groups.map(it => [
      it,
      menudef.option_groups[it],
    ]),
  );
  if (onlyRequired) {
    return Object.values(option_groups).some(it => (it?.min ?? 0) > 0);
  } else {
    return Object.keys(option_groups).length > 0;
  }
};

const shouldAutoOpenOptions = (
  menudef: rs.MenuDef | null,
  foodSet: FoodSet,
) => {
  return (
    menudef &&
    foodSet?.value != null &&
    foodSet?.secId != null &&
    hasOptions(menudef, foodSet.value, true)
  );
};

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    reset: (state, action) => {
      state = initialState;
    },
    updateShopId: (state, action) => {
      state.shopId = action.payload;
      state.titleHeader = `Table: ${
        !isNaN(action.payload) && Number(action.payload) < 9 ? '0' : ''
      }${action.payload}`;
    },
    // for the whole set, used in OrderCheckout
    incrementSet: (
      state,
      action: { payload: { delta: number; setId: string } },
    ) => {
      if (!canModifySelectedMenus(state))
        throw new Error('!canModifySelectedMenus');
      const { delta, setId } = action.payload;
      const set = state.dataSelectedMenus.find(s => s.id === setId);
      if (set) {
        let quantity = set.quantity + delta;
        if (quantity >= 1) {
          set.quantity = quantity;
        } else {
          state.dataSelectedMenus = state.dataSelectedMenus.filter(
            item => item.id !== set.id,
          );
        }
      }
    },
    addIdCategory: (state, action) => {
      state.idCategory = action.payload;
    },
    addTables: (state, action) => {
      state.tables = action.payload;
    },
    addListMenuSets: (state, action) => {
      state.listMenuSets = action.payload;
    },
    openFoodSet: (
      state,
      action: {
        payload: {
          selectedSet: rs.MenuSet;
          draftSet: rs.OrderMenuSet;
          foodSet: FoodSet;
        };
      },
    ) => {
      state.selectedSet = action.payload.selectedSet;
      state.draftSet = action.payload.draftSet;
      state.foodSet = action.payload.foodSet;
      if (shouldAutoOpenOptions(state.menuDef, state.foodSet)) {
        state.optionsPage = {
          foodId: state.foodSet.value!,
          sectionId: state.foodSet.secId!,
        };
      } else {
        state.optionsPage = undefined;
      }
    },
    onClearCart: state => {
      state.selectedSet = {} as rs.MenuSet;
      state.dataSelectedMenus = [];
    },
    onEditMenu: (state, action) => {
      state.isEditMenu = action.payload;
    },
    setSelectedOptions: (
      state,
      action: { payload: Record<string, rs.OrderOption> },
    ) => {},
    setSession: (state, action: { payload: ISessionState; type: string }) => {
      // assume the session changed
      state.session = action.payload;
      state.fullOrder = null;
      state.loadOrderStatus = null;

      state.dataSelectedMenus = [];
      state.costDetail = {
        totalCost: 0,
        basePrice: 0,
      };
      state.listMenuSets = {};
      state.foodSet = null;
    },
    setDraftSet: (state, action: { payload: rs.OrderMenuSet }) => {
      state.draftSet = action.payload;
    },
    draftSetComment: (state, action) => {
      if (state.draftSet == null) return;
      state.draftSet.comment = action.payload;
    },
    // finished editing (some) options of the draft set
    draftSetFillOptions: (state, action: { payload: rs.OrderItem[] }) => {
      if (state.draftSet == null) return;
      let pIx = 0;
      let sfoods = state.draftSet.sections[state.optionsPage!.sectionId]!;
      // not adding more items here
      for (let sIx = 0; sIx < sfoods.length; sIx++) {
        let sfood = sfoods[sIx];
        if (sfood.food_id == state.optionsPage!.foodId) {
          sfood.options = action.payload[pIx].options;
          pIx++;
        }
      }
    },
    openGroupOptions: (state, action: { payload: IGroupOptionsState }) => {
      state.optionsPage = action.payload;
    },
    // in the draft set
    incrementFood: (
      state,
      action: { payload: { sectionId: string; foodId: string } },
    ) => {
      if (state.draftSet!.sections[action.payload.sectionId] == null) {
        state.draftSet!.sections[action.payload.sectionId] = [];
      }
      let section = state.draftSet!.sections[action.payload.sectionId]!;
      let secDef =
        state.menus[state.draftSet!.menuset_id].sections[
          action.payload.sectionId
        ];

      // if max == 1 then radio
      let newItem = {
        food_id: action.payload.foodId,
        options: {},
      };
      if (secDef.max === 1) {
        section = [newItem];
      } else if (secDef.max == null || section.length < secDef.max) {
        section.push(newItem);
      } else return;

      state.draftSet!.sections[action.payload.sectionId] = section;
    },
    decrementFood: (
      state,
      action: { payload: { sectionId: string; foodId: string } },
    ) => {
      let section = state.draftSet!.sections[action.payload.sectionId] ?? [];
      // upgrade typescript to fix this
      let pos = section.findLastIndex(
        it => it.food_id == action.payload.foodId,
      );
      if (pos != -1) {
        section.splice(pos, 1);
      }
    },
    confirmDraftSet: (state, action) => {
      if (state.draftSet == null) return;
      if (!canModifySelectedMenus(state))
        throw new Error('!canModifySelectedMenus');
      state.draftSet.source =
        state.session.kind == 'Table' ? 'WebTable' : 'WebTakeout';
      if (state.isEditMenu) {
        let ix = null;
        state.dataSelectedMenus.forEach((item, index) => {
          if (item.id === state.draftSet!.id) {
            ix = index;
          }
        });

        if (ix != null) {
          state.dataSelectedMenus[ix] = state.draftSet;
          return;
        }
        // we append if index not found
      }

      if (state.draftSet.id != null)
        console.warn(
          'Did not expect (not edited) draftSet to have id assigned in confirmDraftSet',
        );
      state.draftSet.id = gentoken();
      state.dataSelectedMenus.push(state.draftSet);

      // update prices
      if (state.menuDef != null) {
        const prices = updateSetPrices(
          state.dataSelectedMenus,
          state.fullOrder,
          state.menuDef,
        );
        if (prices != null) {
          state.priceMenus = { ...state.priceMenus, ...prices.prices };
          state.costDetail = {
            totalCost: prices.totalPrice,
            basePrice: prices.basePrice,
          };
        }
      }
    },
    setPriceMenus: (
      state,
      action: {
        payload: { prices: Record<string, number>; totalPrice: number };
      },
    ) => {
      state.priceMenus = { ...state.priceMenus, ...action.payload.prices };
      state.costDetail = {
        ...state.costDetail,
        totalCost: action.payload.totalPrice,
      };
    },
    setMenuDef: (state, action: { payload: rs.MenuDef }) => {
      storeMenuDef(state, action.payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(getMenuThunk.fulfilled, (state, action) => {
      storeMenuDef(state, action.payload);
    });
    builder.addCase(postOrderThunk.pending, (state, action) => {
      state.submittingStatus = 'pending';
    });
    builder.addCase(postOrderThunk.rejected, (state, action) => {
      state.submittingStatus = 'error';
    });
    builder.addCase(postOrderThunk.fulfilled, (state, action) => {
      state.submittingStatus = null;
    });

    builder.addCase(postItemsToOrderThunk.pending, (state, action) => {
      state.submittingStatus = 'pending';
      console.log('post items pending');
    });
    builder.addCase(postItemsToOrderThunk.rejected, (state, action) => {
      state.submittingStatus = 'error';
      console.log('post items rejected', action.error);
    });
    builder.addCase(postItemsToOrderThunk.fulfilled, (state, action) => {
      // currently no support adding items while submitting is in progress by taking snapshot
      state.dataSelectedMenus = [];
      state.submittingStatus = null; // nothing to submit
      console.log('post items fulfilled');
    });

    builder.addCase(getCurrentOrderThunk.rejected, (state, action) => {
      console.log('getCurrentOrderThunk rejected', action.error);
      state.loadOrderStatus = 'error';
    });
    builder.addCase(getCurrentOrderThunk.pending, (state, action) => {
      console.log('getCurrentOrder pending');
      state.loadOrderStatus = 'pending';
    });
    builder.addCase(getCurrentOrderThunk.fulfilled, (state, action) => {
      let [menuDef, fullOrder, prices] = action.payload;
      if (state.menuDef == null && menuDef != null)
        storeMenuDef(state, menuDef);
      state.fullOrder = fullOrderMergeStrategy(state.fullOrder, fullOrder);
      state.costDetail = {
        totalCost: prices?.totalPrice ?? 0,
        basePrice: prices?.basePrice ?? 0,
      };
      state.priceMenus = prices?.prices ?? {};

      console.log('getCurrentOrder fulfilled');
    });
  },
});

function paymodsMergeStrategy(
  local: rs.OrderPayMod[] | undefined,
  remote: rs.OrderPayMod[],
) {
  return remote; // place shrug emoji here
}

function fullOrderMergeStrategy(
  local: rs.OrderContainer | null,
  remote: rs.OrderContainer,
): rs.OrderContainer {
  let deleted_items = _.union(
    local?.table.json.deleted_items ?? [],
    remote.table.json.deleted_items,
  );
  let sets: Record<string, rs.OrderMenuSet> = {};
  remote.table.json.menu_items.forEach(it => (sets[it.id] = it));
  deleted_items.forEach(it => delete sets[it]);

  return {
    payments: paymodsMergeStrategy(local?.payments, remote.payments),
    table: {
      ...remote.table,
      json: {
        ...remote.table.json,
        menu_items: Object.values(sets),
        deleted_items,
      },
    },
  };
}

export const {
  onClearCart,
  openFoodSet,
  addIdCategory,
  addListMenuSets,
  updateShopId,
  onEditMenu,
  setSession,
  incrementSet,
  setSelectedOptions,
  draftSetFillOptions,
  openGroupOptions,
  incrementFood,
  setDraftSet,
  decrementFood,
  confirmDraftSet,
  draftSetComment,
  setPriceMenus,
  reset,
  setMenuDef,
  addTables,
} = orderSlice.actions;

export default orderSlice.reducer;
